/**
 * Search, filters and the summary strip — one definition for all three.
 *
 * The strip's "Awaiting tracking" and the "Missing tracking" toggle ask the
 * same question, so they share `isAwaitingTracking`. If they used different
 * predicates the toggle would show a count the strip disagrees with, and the
 * reader would stop trusting both.
 *
 * Pure, so the real API's tests and the URL agree.
 */
import type { MappableStatus, SupplierStatus } from "@/modules/SupplierOrders";

import type {
  BoardSummary,
  DeadlineWindow,
  PortalBoardFilters,
  PortalOrderCard,
  WorkOnWindow,
} from "../types.ts";
import { DEFAULT_BOARD_WINDOW, parseBoardWindow } from "../../SupplierOrders/index.ts";
import { DEFAULT_SORT, isBoardSort } from "./boardSort.ts";
import { addDays, daysBetween, startOfWeek } from "../../../lib/partnerBoard/dates.ts";

export const DEFAULT_PORTAL_FILTERS: PortalBoardFilters = {
  window: DEFAULT_BOARD_WINDOW,
  search: "",
  method: "all",
  deadline: "any",
  workOn: "any",
  missingTracking: false,
  onHold: false,
  sort: DEFAULT_SORT,
};

export interface BoardContext {
  today: string;
  /** Column id → the status it reports into. */
  mappedStatusOf: (columnId: string) => MappableStatus | null;
}

export function boardContext(today: string, statuses: readonly SupplierStatus[]): BoardContext {
  const map = new Map(statuses.map((s) => [s.id, s.mappedStatus]));
  return { today, mappedStatusOf: (columnId) => map.get(columnId) ?? null };
}

/** Still being worked — everything short of Delivered. */
export function isOpen(card: Pick<PortalOrderCard, "columnId">, ctx: BoardContext): boolean {
  return ctx.mappedStatusOf(card.columnId) !== "delivered";
}

/**
 * In Shipping or Shipped with at least one item in no package.
 *
 * Before Shipping, no tracking is expected, so nothing is "missing".
 */
export function isAwaitingTracking(
  card: Pick<PortalOrderCard, "columnId" | "tracking" | "items">,
  ctx: BoardContext,
): boolean {
  const status = ctx.mappedStatusOf(card.columnId);
  if (status !== "shipping" && status !== "shipped") return false;
  return (card.tracking?.coveredItems ?? 0) < card.items.length;
}

export function isOverdue(card: Pick<PortalOrderCard, "deadline" | "columnId">, ctx: BoardContext): boolean {
  return Boolean(card.deadline && card.deadline < ctx.today && isOpen(card, ctx));
}

/** Due within the next seven days. Overdue has its own option and is excluded. */
export function isDueThisWeek(card: Pick<PortalOrderCard, "deadline">, today: string): boolean {
  if (!card.deadline) return false;
  const days = daysBetween(today, card.deadline);
  return days >= 0 && days <= 7;
}

export function matchesDeadline(card: Pick<PortalOrderCard, "deadline" | "columnId">, window: DeadlineWindow, ctx: BoardContext): boolean {
  if (window === "any") return true;
  if (window === "overdue") return isOverdue(card, ctx);
  return isDueThisWeek(card, ctx.today);
}

export function matchesWorkOn(workOn: string | null, window: WorkOnWindow, today: string): boolean {
  if (window === "any") return true;
  if (!workOn) return false;
  if (window === "today") return workOn === today;
  if (window === "tomorrow") return workOn === addDays(today, 1);
  const monday = startOfWeek(today);
  return workOn >= monday && workOn < addDays(monday, 7);
}

/**
 * What search looks through: order id, client, product, Habits product code
 * and the supplier's own code. `aliases` carries old ids of orders merged into
 * this one, so a recalled id stays findable.
 */
export function searchHaystack(card: PortalOrderCard, aliases: readonly string[] = []): string {
  return [
    card.label,
    card.orderNumber,
    card.title,
    card.client,
    ...card.items.flatMap((item) => [item.code, item.name, item.upi, item.supplierCode ?? ""]),
    ...aliases,
  ]
    .join(" ")
    .toLowerCase();
}

export function matchesFilters(
  card: PortalOrderCard,
  filters: PortalBoardFilters,
  ctx: BoardContext,
  aliases: readonly string[] = [],
): boolean {
  const query = filters.search.trim().toLowerCase();
  if (query && !searchHaystack(card, aliases).includes(query)) return false;
  if (filters.method !== "all" && !card.items.some((item) => item.method === filters.method)) return false;
  if (!matchesDeadline(card, filters.deadline, ctx)) return false;
  if (!matchesWorkOn(card.workOn, filters.workOn, ctx.today)) return false;
  if (filters.missingTracking && !isAwaitingTracking(card, ctx)) return false;
  if (filters.onHold && !card.hold) return false;
  return true;
}

/** The four counts, over the supplier's whole board — filters do not change them. */
export function summarize(cards: readonly PortalOrderCard[], ctx: BoardContext): BoardSummary {
  const open = cards.filter((card) => isOpen(card, ctx));
  return {
    open: open.length,
    dueThisWeek: open.filter((card) => isDueThisWeek(card, ctx.today)).length,
    overdue: open.filter((card) => isOverdue(card, ctx)).length,
    awaitingTracking: open.filter((card) => isAwaitingTracking(card, ctx)).length,
  };
}

/** Distinct decoration methods for the filter, in the order first seen. */
export function methodsOf(cards: readonly PortalOrderCard[]): string[] {
  return [...new Set(cards.flatMap((card) => card.items.map((item) => item.method)))];
}

/* ── URL state ─────────────────────────────────────────────────────────── */

const DEADLINES: DeadlineWindow[] = ["any", "week", "overdue"];
const WORK_ON: WorkOnWindow[] = ["any", "today", "tomorrow", "week"];

/** Filters live in the URL so a filtered board is a link someone can share. */
export function parseFilters(params: URLSearchParams): PortalBoardFilters {
  const deadline = params.get("deadline") as DeadlineWindow | null;
  const workOn = params.get("workOn") as WorkOnWindow | null;
  const sort = params.get("sort");
  return {
    window: parseBoardWindow(params.get("window")),
    search: params.get("q") ?? "",
    method: params.get("method") ?? "all",
    deadline: deadline && DEADLINES.includes(deadline) ? deadline : "any",
    workOn: workOn && WORK_ON.includes(workOn) ? workOn : "any",
    missingTracking: params.get("tracking") === "missing",
    onHold: params.get("hold") === "1",
    sort: isBoardSort(sort) ? sort : DEFAULT_SORT,
  };
}

/** Writes only what differs from the default, onto a copy of `base`. */
export function serializeFilters(filters: PortalBoardFilters, base = new URLSearchParams()): URLSearchParams {
  const params = new URLSearchParams(base);
  const set = (key: string, value: string | null) => (value ? params.set(key, value) : params.delete(key));
  set("window", filters.window !== DEFAULT_BOARD_WINDOW ? filters.window : null);
  set("q", filters.search.trim() || null);
  set("method", filters.method !== "all" ? filters.method : null);
  set("deadline", filters.deadline !== "any" ? filters.deadline : null);
  set("workOn", filters.workOn !== "any" ? filters.workOn : null);
  set("tracking", filters.missingTracking ? "missing" : null);
  set("hold", filters.onHold ? "1" : null);
  set("sort", filters.sort !== DEFAULT_SORT ? filters.sort : null);
  return params;
}

/**
 * Sort only reorders and the window sets what the board reads, so neither
 * counts as a filter or shows "Clear filters".
 */
export function hasActiveFilters(filters: PortalBoardFilters): boolean {
  return serializeFilters({ ...filters, sort: DEFAULT_SORT, window: DEFAULT_BOARD_WINDOW }).toString() !== "";
}
